<!-- src/views/management/ManagementMain.vue -->
<template>
  <div class="management-main-page">
    <div class="page-header">
      <h1>🏢 管理层主页</h1>
      <p>管理层功能入口 - 通知管理</p>
    </div>
    
    <!-- 通知公告区域 -->
    <div class="notice-section">
      <div class="notice-header">
        <h3>
          <el-icon><Bell /></el-icon>
          通知公告
          <el-badge :value="unreadCount" class="notice-badge" v-if="unreadCount > 0" />
        </h3>
        <div class="notice-actions">
          <!-- 搜索功能 -->
          <div class="search-container">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索标题或内容..."
              size="small"
              style="width: 180px;"
              clearable
              @keyup.enter="handleSearch"
              @clear="handleSearch"
              prefix-icon="Search"
            />
          </div>
          <el-button size="small" type="primary" plain @click="markAllAsRead">
            <el-icon><Check /></el-icon> 全部已读
          </el-button>
          <el-button size="small" type="success" plain @click="loadNotices">
            <el-icon><Refresh /></el-icon> 刷新
          </el-button>
          <el-button size="small" type="warning" plain @click="showSendDialog">
            <el-icon><Edit /></el-icon> 发送通知
          </el-button>
        </div>
      </div>
      
      <div class="table-wrapper">
        <el-table
          :data="filteredNoticeList"
          border
          style="width:100%"
          v-loading="loading"
          row-key="id"
          :row-class-name="getRowClassName"
          fit
        >
          <el-table-column type="index" label="No" width="50" align="center" />
          
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template #default="{ row }">
              <el-tag v-if="row.isRead" type="info" size="small">已读</el-tag>
              <el-tag v-else type="danger" size="small" effect="dark">未读</el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="title" label="标题" min-width="150" align="left">
            <template #default="{ row }">
              <span class="notice-title" :class="{ 'unread-title': !row.isRead }">
                {{ row.title }}
              </span>
            </template>
          </el-table-column>
          
          <el-table-column prop="content" label="内容" min-width="180" align="left">
            <template #default="{ row }">
              <span class="notice-content">{{ row.content }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="targetRole" label="接收部门" width="120" align="center">
            <template #default="{ row }">
              <el-tag :type="getRoleTagType(row.targetRole)" size="small">
                {{ getRoleLabel(row.targetRole) }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="sender" label="发送人" width="100" align="center">
            <template #default="{ row }">
              <span class="sender-text">{{ row.sender }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="sendTime" label="发送时间" width="160" align="center">
            <template #default="{ row }">
              <span class="time-text">{{ row.sendTime }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="readTime" label="阅读时间" width="160" align="center">
            <template #default="{ row }">
              <span class="time-text">{{ row.readTime || '-' }}</span>
            </template>
          </el-table-column>
          
          <!-- 操作列 -->
          <el-table-column label="操作" width="100" align="center" fixed="right">
            <template #default="{ row }">
              <el-button
                type="danger"
                size="small"
                plain
                @click="handleDelete(row)"
                style="font-size:11px;padding:2px 8px;height:22px;"
              >
                <el-icon><Delete /></el-icon> 删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <!-- 空状态 -->
      <div v-if="!loading && filteredNoticeList.length === 0" class="empty-state">
        <el-empty :description="emptyDescription" />
      </div>
    </div>
    
    <!-- 发送通知对话框 -->
    <el-dialog
      v-model="sendDialogVisible"
      title="发送通知"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form :model="sendForm" label-width="100px" :rules="sendRules" ref="sendFormRef">
        <el-form-item label="接收部门" prop="targetRole">
          <el-select v-model="sendForm.targetRole" placeholder="请选择接收部门" style="width:100%">
            <el-option label="全部部门" value="all" />
            <el-option label="运营部" value="operation" />
            <el-option label="管理层" value="management" />
            <el-option label="市场部" value="market" />
            <el-option label="财务部" value="finance" />
          </el-select>
        </el-form-item>
        
        <el-form-item label="通知标题" prop="title">
          <el-input v-model="sendForm.title" placeholder="请输入通知标题" maxlength="100" show-word-limit />
        </el-form-item>
        
        <el-form-item label="通知内容" prop="content">
          <el-input 
            v-model="sendForm.content" 
            type="textarea" 
            placeholder="请输入通知内容"
            :rows="6"
            maxlength="1000"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="sendDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="sendNotice" :loading="sending">
          发送
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Bell, Refresh, Check, Edit, Delete } from '@element-plus/icons-vue'

// ============ 状态 ============
const loading = ref(false)
const sending = ref(false)
const noticeList = ref([])
const searchKeyword = ref('')
const sendDialogVisible = ref(false)
const sendFormRef = ref(null)
let pollingTimer = null

// ============ 发送表单 ============
const sendForm = ref({
  targetRole: 'all',
  title: '',
  content: ''
})

const sendRules = {
  targetRole: [{ required: true, message: '请选择接收部门', trigger: 'change' }],
  title: [{ required: true, message: '请输入通知标题', trigger: 'blur' }],
  content: [{ required: true, message: '请输入通知内容', trigger: 'blur' }]
}

// ============ 计算属性 ============
const unreadCount = computed(() => {
  return noticeList.value.filter(item => !item.isRead).length
})

const filteredNoticeList = computed(() => {
  if (!searchKeyword.value.trim()) {
    return noticeList.value
  }
  const keyword = searchKeyword.value.trim().toLowerCase()
  return noticeList.value.filter(item => 
    item.title.toLowerCase().includes(keyword) ||
    item.content.toLowerCase().includes(keyword)
  )
})

const emptyDescription = computed(() => {
  if (searchKeyword.value.trim()) {
    return '未找到匹配的通知'
  }
  return '暂无通知，点击「发送通知」创建'
})

// ============ 工具函数 ============
const getRoleLabel = (role) => {
  const map = {
    'all': '全部部门',
    'operation': '运营部',
    'management': '管理层',
    'market': '市场部',
    'finance': '财务部'
  }
  return map[role] || role || '全部'
}

const getRoleTagType = (role) => {
  const map = {
    'all': 'info',
    'operation': 'success',
    'management': 'warning',
    'market': 'primary',
    'finance': 'danger'
  }
  return map[role] || 'info'
}

// ============ API 配置 ============
const getApiBase = () => {
  return process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000'
}

const getHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

// ============ 数据映射 ============
const mapDbToFrontend = (dbData) => {
  return {
    id: dbData.id,
    title: dbData.title || '',
    content: dbData.content || '',
    sender: dbData.sender || '管理层',
    targetRole: dbData.target_role || 'all',
    sendTime: dbData.send_time ? formatDateTime(dbData.send_time) : '',
    isRead: dbData.is_read || false,
    readTime: dbData.read_time ? formatDateTime(dbData.read_time) : null
  }
}

const formatDateTime = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ============ 获取通知列表 ============
const fetchNotices = async () => {
  try {
    const response = await fetch(`${getApiBase()}/api/auth/notices?role=management`, {
      headers: getHeaders()
    })
    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return []
    }
    if (!response.ok) throw new Error('获取通知列表失败')
    const result = await response.json()
    return (result.data || []).map(mapDbToFrontend)
  } catch (error) {
    console.error('获取通知列表错误:', error)
    throw error
  }
}

const loadNotices = async () => {
  if (loading.value) return
  loading.value = true
  try {
    const data = await fetchNotices()
    noticeList.value = data
  } catch (error) {
    ElMessage.error('加载通知列表失败')
  } finally {
    loading.value = false
  }
}

// ============ 搜索 ============
const handleSearch = () => {
  // 搜索逻辑已通过 computed 实现
}

// ============ 标记已读 ============
const markAllAsRead = async () => {
  const unreadItems = noticeList.value.filter(item => !item.isRead)
  if (unreadItems.length === 0) {
    ElMessage.info('没有未读通知')
    return
  }
  
  try {
    const ids = unreadItems.map(item => item.id)
    const response = await fetch(`${getApiBase()}/api/auth/notices/read-all`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ ids, role: 'management' })
    })
    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }
    if (!response.ok) throw new Error('批量标记失败')
    
    unreadItems.forEach(item => item.isRead = true)
    ElMessage.success(`已标记 ${unreadItems.length} 条通知为已读`)
  } catch (error) {
    ElMessage.error(`操作失败: ${error.message}`)
  }
}

// ============ 发送通知 ============
const showSendDialog = () => {
  sendForm.value = {
    targetRole: 'all',
    title: '',
    content: ''
  }
  sendDialogVisible.value = true
}

const sendNotice = async () => {
  if (!sendFormRef.value) return
  await sendFormRef.value.validate(async (valid) => {
    if (!valid) return
    
    sending.value = true
    try {
      const response = await fetch(`${getApiBase()}/api/auth/notices`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          title: sendForm.value.title,
          content: sendForm.value.content,
          target_role: sendForm.value.targetRole,
          sender: '管理层'
        })
      })
      if (response.status === 401) {
        ElMessage.error('认证已过期，请重新登录')
        setTimeout(() => window.location.href = '/login', 1500)
        return
      }
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '发送失败')
      }
      
      const result = await response.json()
      if (result.success) {
        ElMessage.success('通知发送成功！')
        sendDialogVisible.value = false
        await loadNotices()
      } else {
        ElMessage.error(result.message || '发送失败')
      }
    } catch (error) {
      ElMessage.error(`发送失败: ${error.message}`)
    } finally {
      sending.value = false
    }
  })
}

// ============ 删除通知 ============
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除通知 "${row.title}" 吗？\n删除后该通知将永久移除，且所有用户将无法查看。`,
      '删除确认',
      {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    const response = await fetch(`${getApiBase()}/api/auth/notices/${row.id}`, {
      method: 'DELETE',
      headers: getHeaders()
    })
    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '删除失败')
    }
    
    const result = await response.json()
    if (result.success) {
      ElMessage.success(`通知 "${row.title}" 已删除`)
      await loadNotices()
    } else {
      ElMessage.error(result.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(`删除失败: ${error.message}`)
    }
  }
}

// ============ 行样式 ============
const getRowClassName = ({ row }) => {
  return row.isRead ? 'read-row' : 'unread-row'
}

// ============ 轮询获取新通知 ============
const startPolling = () => {
  pollingTimer = setInterval(async () => {
    try {
      const data = await fetchNotices()
      if (data.length > noticeList.value.length) {
        noticeList.value = data
        ElMessage.info(`有 ${data.length - noticeList.value.length} 条新通知`)
      } else {
        const oldMap = new Map(noticeList.value.map(item => [item.id, item]))
        data.forEach(newItem => {
          const oldItem = oldMap.get(newItem.id)
          if (oldItem && oldItem.isRead !== newItem.isRead) {
            oldItem.isRead = newItem.isRead
          }
        })
      }
    } catch (error) {
      console.debug('轮询通知失败:', error)
    }
  }, 30000)
}

// ============ 生命周期 ============
onMounted(() => {
  loadNotices()
  startPolling()
})

onBeforeUnmount(() => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
})
</script>

<style scoped>
.management-main-page {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.page-header {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.page-header h1 {
  font-size: 24px;
  color: #1a2332;
  font-weight: 700;
}

.page-header p {
  color: #6c7a8a;
  margin-top: 4px;
  font-size: 14px;
}

/* ============ Notice 区域 ============ */
.notice-section {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.notice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #ebeef5;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
}

.notice-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notice-badge {
  margin-left: 4px;
}

.notice-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.search-container {
  display: flex;
  align-items: center;
}

.search-container .el-input {
  width: 180px;
}

.search-container .el-input :deep(.el-input__wrapper) {
  height: 30px;
  min-height: 30px;
}

.search-container .el-input :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  font-size: 12px;
}

.table-wrapper {
  flex: 1;
  overflow: auto;
  padding-top: 12px;
  min-height: 0;
}

/* ============ 表格样式 ============ */
:deep(.el-table) {
  font-size: 13px;
  width: 100% !important;
}

:deep(.el-table .cell) {
  padding: 2px 4px;
}

:deep(.el-table td.el-table__cell) {
  padding: 2px 0 !important;
}

:deep(.el-table__header-wrapper) {
  background: #f5f7fa;
}

:deep(.el-table th.el-table__cell) {
  background-color: #3498db !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 11px !important;
  padding: 4px 0 !important;
}

:deep(.el-table th.el-table__cell .cell) {
  font-size: 11px !important;
  line-height: 20px !important;
  color: #ffffff !important;
}

/* 行样式 */
:deep(.unread-row) {
  background-color: #fef6f6 !important;
  font-weight: 500;
}

:deep(.unread-row:hover) {
  background-color: #fde8e8 !important;
}

:deep(.read-row) {
  background-color: #ffffff !important;
}

:deep(.read-row:hover) {
  background-color: #f5f7fa !important;
}

.notice-title {
  font-size: 13px;
  color: #303133;
}

.unread-title {
  font-weight: 600;
  color: #1a2332;
}

.notice-content {
  font-size: 12px;
  color: #606266;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sender-text {
  font-size: 12px;
  color: #606266;
}

.time-text {
  font-size: 12px;
  color: #909399;
}

/* ============ 空状态 ============ */
.empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

/* ============ 滚动条 ============ */
.table-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}

/* ============ 发送对话框 ============ */
:deep(.el-dialog) {
  border-radius: 12px;
}

:deep(.el-dialog__header) {
  border-bottom: 1px solid #ebeef5;
  padding: 16px 20px;
}

:deep(.el-dialog__body) {
  padding: 20px;
}

:deep(.el-dialog__footer) {
  border-top: 1px solid #ebeef5;
  padding: 12px 20px;
}

/* ============ 删除按钮样式 ============ */
:deep(.el-button .el-icon) {
  margin-right: 2px;
}
</style>